// assets/auto-add.js

import { ThemeEvents } from '@theme/events';

// ============================
// Auto-add item settings
// ============================

const AUTO_ADD_VARIANT_ID = 50887146832157;

// "once_per_cart"  → 1 item if cart has any non-items
// "per_item"       → quantity mirrors total non-quantity
const AUTO_ADD_MODE = 'once_per_cart'; // or 'per_item'

// Line item properties for the item
const AUTO_ADD_PROPERTIES = {
  '__hidden': 'true',
};

let isSyncingAddItem = false;

// Fetch current cart JSON
async function fetchCart() {
  const res = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!res.ok) throw new Error('Failed to load cart.js');
  return res.json();
}

// Decide how many items we *should* have based on Product Type
function calculateDesiredAddQuantity(cart) {
  const addVariantId = AUTO_ADD_VARIANT_ID;
  let nonAddQuantity = 0;
  let corporateItemCount = 0;
  let itemCountMinusAutoAdd = 0;

  (cart.items || []).forEach((item) => {
    // 1. Skip the auto-added item itself
    if (item.variant_id != addVariantId) {
      nonAddQuantity += item.quantity || 0;

      // 2. Check if this product's type is 'corporateapp'
      if (item.product_type === 'corporateapp') {
        corporateItemCount += 1;
      }

      itemCountMinusAutoAdd += 1;
    }
  });

  // LOGIC: If only corporate item is present, we remove/don't add the hidden item.
  if (corporateItemCount === itemCountMinusAutoAdd) {
    return 0;
  }

  // STANDARD LOGIC: If no corporate item, proceed based on mode
  if (AUTO_ADD_MODE === 'once_per_cart') {
    return nonAddQuantity > 0 ? 1 : 0;
  }

  if (AUTO_ADD_MODE === 'per_item') {
    return nonAddQuantity;
  }

  return 0;
}

// Ensure item matches desired quantity
async function syncAddItem(cart) {
  if (!cart || isSyncingAddItem) return;

  isSyncingAddItem = true;

  try {
    const desiredQty = calculateDesiredAddQuantity(cart);
    const addVariantId = AUTO_ADD_VARIANT_ID;

    const items = cart.items || [];
    const addItem = items.find((item) => item.variant_id === addVariantId);

    // Case 1: no item but we need one → add
    if (!addItem && desiredQty > 0) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: addVariantId,
          quantity: desiredQty,
          properties: AUTO_ADD_PROPERTIES,
        }),
      });
      return;
    }

    // Case 2: exists but not needed anymore → remove
    if (addItem && desiredQty === 0) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: addItem.key, // line item key
          quantity: 0,
        }),
      });
      return;
    }

    // Case 3: exists but quantity is wrong → update qty (and ensure __hidden stays)
    if (addItem && addItem.quantity !== desiredQty) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: addItem.key,
          quantity: desiredQty,
          properties: {
            ...(addItem.properties || {}),
            ...AUTO_ADD_PROPERTIES,
          },
        }),
      });
    }
  } catch (err) {
    console.error('Error syncing cart item:', err);
  } finally {
    isSyncingAddItem = false;
  }
}

// Initial sync + hook into Heritage/Horizon cart events
document.addEventListener('DOMContentLoaded', async () => {
  // Initial run (in case cart already has items)
  try {
    const cart = await fetchCart();
    await syncAddItem(cart);
  } catch (err) {
    console.error('Error loading initial cart:', err);
  }

  // Listen for Horizon/Heritage cart updates
  document.addEventListener(ThemeEvents.cartUpdate, async () => {
    try {
      const cart = await fetchCart();
      await syncAddItem(cart);
    } catch (err) {
      console.error('Error syncing item on cart update:', err);
    }
  });
});
